// CanvasAppt — Ship 8 (Apr 30, 2026)
// Canvasser-side appointment form. Tap from a knock-pin's "App" button →
// /canvas/appt?pin={id}. Captures homeowner contact info, insurance, appt
// time, sms_consent, notes. Submits to pending_canvas_appointments. Alex
// (or any manager) approves via /canvas/approvals → edge function creates
// a deals row at appointment_received → auto-trigger fires storm-report-prepare.
//
// Oct 27, 2026 — "Log a lead" by address: /canvas/appt?manual=1 (from a
// canvasser's home, or the map's address search when the house has no pin;
// street/city/state/zip may come in the URL). Same pending row and the same
// approval, so it counts toward the canvasser's per-appointment pay exactly
// like an App from the map. A pin at exactly that address is attached only
// when the map would let him book it (not built, a past customer, a claim,
// already booked, a lead or do-not-knock, and not on another deal); otherwise
// the approval geocodes the address. Canvassers can't drop pins.

import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";

interface OwnershipHint {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  stage: string | null;
  owner_name: string | null;
}

interface KnockPin {
  id: string;
  property_street: string;
  property_city: string;
  property_state: string;
  property_zip: string | null;
  homeowner_name: string | null;
  current_state: string;
}

const COMMON_CARRIERS = [
  "State Farm", "Allstate", "USAA", "Liberty Mutual", "Travelers",
  "Farmers", "Nationwide", "Progressive", "American Family",
  "Erie", "Auto-Owners", "Safeco", "Other",
];

export default function CanvasAppt() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { member } = useAuth();
  const pinId = searchParams.get("pin");
  const manual = !pinId && searchParams.get("manual") === "1";

  const [pin, setPin] = useState<KnockPin | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [insurance, setInsurance] = useState("");
  const [otherCarrier, setOtherCarrier] = useState("");
  const [roofAge, setRoofAge] = useState<string>("");
  // Pre-fill notes from the canvasser's door-side note if Canvas passed one
  // through the ?notes= query param (Fix #4, May 11, 2026). Canvasser can
  // edit before submit.
  const [notes, setNotes] = useState<string>(() => searchParams.get("notes") ?? "");
  const [smsConsent, setSmsConsent] = useState(true);

  // Manual mode: the address is typed (or handed over by the map search).
  const [street, setStreet] = useState(() => searchParams.get("street") ?? "");
  const [city, setCity] = useState(() => searchParams.get("city") ?? "");
  const [stateCode, setStateCode] = useState(() => (searchParams.get("state") || "GA").toUpperCase().slice(0, 2));
  const [zip, setZip] = useState(() => (searchParams.get("zip") ?? "").replace(/\D/g, "").slice(0, 5));
  const [hints, setHints] = useState<OwnershipHint[]>([]);
  const [mapPin, setMapPin] = useState<AddressPin | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  // Load pin
  useEffect(() => {
    if (manual) { setLoading(false); return; }
    if (!pinId) {
      setErr("Missing pin id. Tap a pin from /canvas first.");
      setLoading(false);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from("knock_pins")
        .select("id, property_street, property_city, property_state, property_zip, homeowner_name, current_state")
        .eq("id", pinId)
        .maybeSingle();
      if (error) setErr(error.message);
      if (data) {
        setPin(data as KnockPin);
        // Pre-fill name if known
        if (data.homeowner_name) {
          const parts = String(data.homeowner_name).trim().split(/\s+/);
          setFirst(parts[0] ?? "");
          setLast(parts.slice(1).join(" "));
        }
      }
      setLoading(false);
    })();
  }, [pinId, manual]);

  // Manual mode: is this house already somebody's live deal? Read-only hint
  // (owner + stage) so the canvasser knows before sending; Alex still decides.
  useEffect(() => {
    if (!manual) return;
    const st = street.trim();
    const ct = city.trim();
    if (!/^\s*\d+\s+[A-Za-z]/.test(st) || ct.length < 2) { setHints([]); setMapPin(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      const [{ data }, pinHere] = await Promise.all([
        supabase.rpc("find_deal_ownership_hints", {
          p_street: st, p_city: ct, p_phone: phone.replace(/\D/g, "").length >= 10 ? phone : null,
        }),
        pinAtAddress(st, ct),
      ]);
      if (cancelled) return;
      setHints(Array.isArray(data) ? (data as OwnershipHint[]) : []);
      setMapPin(pinHere);
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [manual, street, city, phone]);

  const address = pin
    ? { street: pin.property_street, city: pin.property_city, state: pin.property_state, zip: pin.property_zip }
    : { street: street.trim(), city: city.trim(), state: stateCode.trim().toUpperCase(), zip: zip.trim() || null };
  const addressOk = !!pin || (manual && /\d/.test(address.street) && address.city.length >= 2 && address.state.length === 2);

  const canSubmit = first.trim() && last.trim() && phone.replace(/\D/g, "").length >= 10
    && addressOk && !submitting;

  async function handleSubmit() {
    if (!canSubmit || !member) return;
    setSubmitting(true);
    setErr(null);
    try {
      const carrier = insurance === "Other" ? otherCarrier.trim() : insurance;
      // No specific appt time captured on the door anymore — we promise the
      // homeowner that the RM will reach out to schedule within 3–5 days.
      // Stamp a placeholder ~3 business days out so downstream readers
      // (Alex's approval queue, the deal's appointment_date column) keep
      // working. Alex / the RM will overwrite this when they confirm the
      // real time.
      const apptIso = estimatedAppointmentAt();

      // Ship 6: capture rep's location at submit time so Alex's queue can
      // render a "🟢 On property / 🟡 Near / 🔴 Far" chip vs the pin's lat/lon.
      // Best-effort — don't block submit if geolocation hangs.
      const submitterPos = await new Promise<{ lat: number; lon: number } | null>((resolve) => {
        if (typeof navigator === "undefined" || !navigator.geolocation) return resolve(null);
        let done = false;
        const fin = (v: { lat: number; lon: number } | null) => { if (!done) { done = true; resolve(v); } };
        navigator.geolocation.getCurrentPosition(
          (p) => fin({ lat: p.coords.latitude, lon: p.coords.longitude }),
          () => fin(null),
          { enableHighAccuracy: true, timeout: 6_000, maximumAge: 30_000 },
        );
        setTimeout(() => fin(null), 6_500);
      });

      // Typed address: attach the knock pin at exactly that house so the map
      // shows the appointment there too, but only one the map would let him
      // book (the pin sheet locks the App button on these states).
      const typedPin = manual ? await pinAtAddress(address.street, address.city) : null;
      const pinIdForRow = pin?.id ?? (typedPin && pinBookable(typedPin) ? typedPin.id : null);

      const { error } = await supabase.from("pending_canvas_appointments").insert({
        knock_pin_id: pinIdForRow,
        submitted_by: member.id,
        homeowner_first: first.trim(),
        homeowner_last: last.trim(),
        homeowner_phone: phone.replace(/\D/g, ""),
        homeowner_email: email.trim() || null,
        property_street: address.street,
        property_city: address.city,
        property_state: address.state,
        property_zip: address.zip,
        insurance_company: carrier || null,
        roof_age_estimate: roofAge ? parseInt(roofAge, 10) : null,
        sms_consent: smsConsent,
        appointment_at: apptIso,
        notes: notes.trim() || null,
        submitter_lat: submitterPos?.lat ?? null,
        submitter_lon: submitterPos?.lon ?? null,
      });
      if (error) throw error;

      // Also log a canvas_visits row with outcome='appt_set' so the pin transitions
      if (pinIdForRow) await supabase.from("canvas_visits").insert({
        knock_pin_id: pinIdForRow,
        visited_by: member.id,
        outcome: "appt_set",
        notes: `Appt submitted for ${first} ${last} — RM to schedule within 3–5 days`,
        appointment_at: apptIso,
        lat: submitterPos?.lat ?? null,
        lon: submitterPos?.lon ?? null,
      });

      // Fix #2+#3 (May 11, 2026): used to navigate("/canvas?submitted=1",
      // { replace: true }) which set a different URL each time and remounted
      // Canvas with fresh state — wiping map pan/zoom and flipping weather
      // toggles back off. Canvas still remounts on route change (true fix
      // would be a modal refactor) but its useState initializers now hydrate
      // from sessionStorage and the moveend handler writes on every pan/zoom,
      // so the canvasser lands back in the same neighborhood with the same
      // layers visible. `replace: true` prevents browser-back from re-opening
      // the just-submitted form.
      if (manual) {
        setSent(`${first.trim()} ${last.trim()} at ${address.street}`);
        setSubmitting(false);
        return;
      }
      navigate("/canvas", { replace: true });
    } catch (e: any) {
      setErr(e.message ?? String(e));
      setSubmitting(false);
    }
  }

  function startOver() {
    setSent(null);
    setFirst(""); setLast(""); setPhone(""); setEmail("");
    setInsurance(""); setOtherCarrier(""); setRoofAge(""); setNotes("");
    setStreet(""); setCity(""); setZip(""); setHints([]);
    setSmsConsent(true);
  }

  if (loading) {
    return <div className="p-4 text-sm text-slate-500">Loading…</div>;
  }
  if (sent) {
    return (
      <div className="min-h-screen bg-slate-100 p-4">
        <div className="mx-auto max-w-lg rounded-xl border border-emerald-200 bg-white p-5 text-center shadow-sm">
          <div className="text-3xl">✅</div>
          <div className="mt-2 text-base font-bold text-slate-900">Sent for approval</div>
          <p className="mt-1 text-sm text-slate-600">
            {sent} is in Alex's approval queue. You'll get a notification when it's approved, and it counts toward your appointment pay once it is.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <button onClick={startOver} className="rounded-md bg-orange-500 px-4 py-2 text-sm font-semibold text-white">Log another</button>
            <Link to="/" className="rounded-md bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">Home</Link>
            <Link to="/canvas" className="rounded-md bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">Canvas</Link>
          </div>
        </div>
      </div>
    );
  }
  if (!pin && !manual) {
    return (
      <div className="p-4">
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">⚠ {err ?? "Pin not found."}</div>
        <button onClick={() => navigate("/canvas")} className="mt-3 rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white">← Back to canvas</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-32">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-slate-200 bg-white px-3 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="rounded-md px-2 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-100">✕</button>
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-900">{manual ? "Log a lead" : "Set Appointment"}</div>
          <div className="text-xs text-slate-500">
            {pin ? `${pin.property_street} · ${pin.property_city}` : "Goes to Alex for approval, the same as an App from the map"}
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {manual ? (
          <FormSection title="Address">
            <Field label="Street" required>
              <input value={street} onChange={(e) => setStreet(e.target.value)} placeholder="123 Main St" className={inputCls} autoComplete="address-line1" />
            </Field>
            <div className="grid grid-cols-[1fr_4rem_5.5rem] gap-2">
              <Field label="City" required>
                <input value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} autoComplete="address-level2" />
              </Field>
              <Field label="State" required>
                <input value={stateCode} onChange={(e) => setStateCode(e.target.value.replace(/[^A-Za-z]/g, "").toUpperCase().slice(0, 2))} className={inputCls} autoComplete="address-level1" />
              </Field>
              <Field label="ZIP">
                <input value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))} inputMode="numeric" className={inputCls} autoComplete="postal-code" />
              </Field>
            </div>
            {mapPin && !pinBookable(mapPin) ? (
              <div className="rounded-md bg-amber-50 p-2.5 text-xs text-amber-900">
                On the map, this house is marked <strong>{LOCKED_LABEL[mapPin.current_state] ?? "taken"}</strong>
                {mapPin.deal_id && !LOCKED_LABEL[mapPin.current_state] ? " (it's on another deal)" : ""}. Check before sending.
                Your lead still goes to Alex, and the map pin stays as it is.
              </div>
            ) : null}
            {hints.length > 0 ? (
              <div className="rounded-md bg-amber-50 p-2.5 text-xs text-amber-900">
                {hints.slice(0, 2).map((h) => (
                  <div key={h.id}>
                    Already in the CRM: {h.homeowner_name ?? "a homeowner"} at {h.property_street ?? "this address"}
                    {h.owner_name ? `, ${h.owner_name}'s lead` : ""}{h.stage ? ` (${h.stage.replace(/_/g, " ")})` : ""}.
                  </div>
                ))}
                <div className="mt-1">You can still send it. Alex decides.</div>
              </div>
            ) : null}
          </FormSection>
        ) : null}
        {/* Homeowner */}
        <FormSection title="Homeowner">
          <div className="grid grid-cols-2 gap-2">
            <Field label="First name" required>
              <input value={first} onChange={(e) => setFirst(e.target.value)} className={inputCls} autoComplete="given-name" />
            </Field>
            <Field label="Last name" required>
              <input value={last} onChange={(e) => setLast(e.target.value)} className={inputCls} autoComplete="family-name" />
            </Field>
          </div>
          <Field label="Phone" required>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="470-555-0123"
              type="tel"
              className={inputCls}
              autoComplete="tel"
              inputMode="tel"
            />
          </Field>
          <Field label="Email">
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="optional"
              type="email"
              className={inputCls}
              autoComplete="email"
              inputMode="email"
            />
          </Field>
        </FormSection>

        {/* Insurance + roof */}
        <FormSection title="Property">
          <Field label="Insurance carrier">
            <select value={insurance} onChange={(e) => setInsurance(e.target.value)} className={inputCls}>
              <option value="">— pick one —</option>
              {COMMON_CARRIERS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>
          {insurance === "Other" ? (
            <Field label="Carrier name">
              <input
                value={otherCarrier}
                onChange={(e) => setOtherCarrier(e.target.value)}
                placeholder="(homeowner-stated)"
                className={inputCls}
              />
            </Field>
          ) : null}
          <Field label="Estimated roof age (years)">
            <input
              value={roofAge}
              onChange={(e) => setRoofAge(e.target.value.replace(/\D/g, ""))}
              placeholder="e.g. 12"
              type="text"
              inputMode="numeric"
              className={inputCls}
            />
          </Field>
        </FormSection>

        {/* Appointment */}
        <FormSection title="Appointment">
          <Field label="Notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Anything Alex / RM should know before they call"
              className={inputCls + " resize-none"}
            />
          </Field>
          <label className="mt-2 flex items-start gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <strong className="text-slate-900">Homeowner agreed to SMS.</strong>{" "}
              Lifecycle texts (appointment confirmation, reminders) only fire when this is on.
            </span>
          </label>
        </FormSection>

        {err ? (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">⚠ {err}</div>
        ) : null}

        <div className="text-xs text-slate-500">
          On submit, this goes to Alex's <strong>Approval queue</strong>. He approves → a deal is
          created + assigned → storm report auto-fires → the RM calls the homeowner within 3–5 days
          to lock the actual appointment time.
        </div>
      </div>

      {/* Floating submit bar */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-slate-200 bg-white p-3 shadow-lg">
        <div className="mx-auto flex max-w-lg items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            disabled={submitting}
            className="rounded-md bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 rounded-md bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "✅ Submit for approval"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-1 focus:ring-orange-500";

// Canvasser no longer captures a specific appt day/time at the door — we tell
// the homeowner "3–5 days". We still stamp a placeholder on the pending row +
// canvas_visit so Alex's queue can render a sortable timestamp and the deal
// gets an appointment_date Alex (or the RM) can overwrite during scheduling.
// Default: today + 3 business days at 10:00 local (Mon–Fri window, skipping
// Sat/Sun). Returns an ISO string.
function estimatedAppointmentAt(now: Date = new Date()): string {
  const d = new Date(now);
  d.setHours(10, 0, 0, 0);
  let added = 0;
  while (added < 3) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay(); // 0=Sun, 6=Sat
    if (day !== 0 && day !== 6) added += 1;
  }
  return d.toISOString();
}

interface AddressPin { id: string; property_street: string | null; current_state: string; deal_id: string | null }

// Pin states the map's pin sheet won't let a rep book (Canvas.tsx
// LOCKED_PIN_STATES), with how to say them.
const LOCKED_LABEL: Record<string, string> = {
  appt_scheduled: "appointment already booked",
  lead: "a lead",
  claim_called: "claim called in",
  built: "a roof we built",
  legacy_client: "a past customer",
  do_not_knock: "do not knock",
};
function pinBookable(p: AddressPin): boolean {
  return !LOCKED_LABEL[p.current_state] && !p.deal_id;
}

// Street words to one spelling, so "123 Oak Drive" and "123 OAK DR." match.
const STREET_WORDS: Record<string, string> = {
  drive: "dr", court: "ct", road: "rd", street: "st", lane: "ln", circle: "cir",
  avenue: "ave", place: "pl", trail: "trl", parkway: "pkwy", boulevard: "blvd",
  terrace: "ter", cove: "cv", point: "pt", crossing: "xing", highway: "hwy",
  square: "sq", ridge: "rdg", bend: "bnd", landing: "lndg", overlook: "ovlk",
  trace: "trce", view: "vw", hollow: "holw", glen: "gln", mount: "mt",
  north: "n", south: "s", east: "e", west: "w",
  northeast: "ne", northwest: "nw", southeast: "se", southwest: "sw",
};
function normStreet(s: string): string {
  return s.toLowerCase().replace(/[.,#]/g, " ").split(/\s+/).filter(Boolean)
    .map((w) => STREET_WORDS[w] ?? w).join(" ");
}

// The knock pin at a typed address: candidates by house number + first
// street word + city, then only an exact match on the whole street (so
// "134 Grand Oaks Dr" never picks up "134 Grandmar Chase"), and only when
// exactly one pin is that house. City is free text, so it's escaped.
async function pinAtAddress(street: string, city: string): Promise<AddressPin | null> {
  const m = street.match(/^\s*(\d+)\s+([A-Za-z]+)/);
  if (!m || !city.trim()) return null;
  const cityPat = city.trim().replace(/[\\%_]/g, (c) => `\\${c}`);
  const { data } = await supabase
    .from("knock_pins")
    .select("id, property_street, current_state, deal_id")
    .ilike("property_street", `${m[1]} ${m[2]}%`)
    .ilike("property_city", cityPat)
    .limit(20);
  const want = normStreet(street);
  const exact = ((data ?? []) as AddressPin[]).filter((p) => p.property_street && normStreet(p.property_street) === want);
  return exact.length === 1 ? exact[0] : null;
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">
        {label}{required ? <span className="ml-0.5 text-red-500">*</span> : null}
      </span>
      {children}
    </label>
  );
}
